/**
 * Maps the columns of an uploaded CSV to the fields the import expects.
 * Extracted from ImportFormContainer.checkHeaders.
 *
 * The back parses the file with csv-parse and `columns: headers`, so
 * `headers[i]` is the name the i-th column of the file gets.
 *
 * Columns that match no expected field are named `sin_asignar_<column>`, which
 * no processRow reads. They used to get the first expected key ("numero" for
 * assets, "clave" for the catalogs): with a 63rd expected column, an old
 * template with a trailing comma left `numero` empty, and a client column at
 * the end ("notas") overwrote `numero` with its value.
 */
export const UNASSIGNED = -1;

export const unassignedColumn = (index) => `sin_asignar_${index + 1}`;

// The template and the CSVs saved by Excel start with a BOM; without stripping
// it the first header ("numero") never matches.
const cleanHeader = (header) => String(header).replace(/^﻿/, '');

/**
 * @param {string[]} expectedKeys - headers[module], plus custom fields for employees
 * @param {string[]} fileHeaders - first line of the CSV, split by commas
 * @param {boolean} [dynamic] - modules with llave_N / etiqueta_N columns
 * @returns {{headers: string[], headersSelect: {key: string, posicion: number}[],
 *   headersOpciones: {index: number, value: string}[]}}
 *   headersSelect.posicion is UNASSIGNED when the file does not have that column.
 */
export const mapImportHeaders = (expectedKeys, fileHeaders, dynamic = false) => {
  const fileKeys = fileHeaders.map(cleanHeader);
  const headersSelect = expectedKeys.map((key) => ({ posicion: UNASSIGNED, key }));

  if (dynamic) {
    fileKeys.forEach((key) => {
      if (key.includes('llave_')) {
        const posicion = Number(key.substring(6));
        headersSelect.push({ posicion: UNASSIGNED, key });
        headersSelect.push({ posicion: UNASSIGNED, key: 'etiqueta_' + posicion });
      }
    });
  }

  const headersOpciones = fileKeys.map((value, index) => ({ index, value }));
  const headers = fileKeys.map((key, index) => {
    let matched = false;
    headersSelect.forEach((select) => {
      if (key === select.key) {
        select.posicion = index;
        matched = true;
      }
    });
    return matched ? key : unassignedColumn(index);
  });

  // Fallback for the key only (the first expected column: numero, clave or
  // nombre). Every template starts with it, and the old mapping gave the first
  // key to any unmatched column, so a misspelled key header ("clabe") in the
  // first column still imported. Kept for that case only: the key is nowhere
  // in the file AND the first column of the file matches nothing.
  const keySelect = headersSelect[0];
  const keyMissing = keySelect && keySelect.posicion === UNASSIGNED;
  const firstColumnFree = headers.length > 0 && headers[0] === unassignedColumn(0);
  if (keyMissing && firstColumnFree) {
    headers[0] = keySelect.key;
    keySelect.posicion = 0;
  }

  return { headers, headersSelect, headersOpciones };
};



// WEBPACK FOOTER //
// src/lib/import/map_import_headers.js